import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { login, setToken } from "../redux/action/login.action";
import { toast } from "../components/AppToast";
import { userLogin } from "../services/auth/auth";

const ROLE_LABELS = {
    SUPER_ADMIN: "Super Admin",
    TENANT_ADMIN: "Tenant Admin",
    PARTNER_ADMIN: "Partner Admin",
    TENANT_USER: "Tenant User",
    PARTNER_USER: "Partner User",
    SUPPORT_TEAM: "Support Team",
    TEMP_USER: "Temporary User",
};

const formatRole = (userType) => {
    if (!userType) return "User";
    if (ROLE_LABELS[userType]) return ROLE_LABELS[userType];
    return String(userType)
        .toLowerCase()
        .split("_")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
};

const SETUP_MS = 2600;

const HUB_CENTER = { x: 49.9, y: 48.7 };

const HUB_NODES = [
    { id: "suppliers", label: "Suppliers", x: 31.5, y: 10.0, place: "left", icon: "chart", pink: false, dotAt: 0.58 },
    { id: "retailers", label: "Retailers", x: 68.3, y: 10.0, place: "right", icon: "store", pink: true, dotAt: 0.58 },
    { id: "3pls", label: "3PLs", x: 13.1, y: 31.1, place: "below", icon: "barsChart", pink: false, dotAt: 0.62 },
    { id: "customers", label: "Customers", x: 86.7, y: 31.1, place: "below", icon: "user", pink: true, dotAt: 0.62 },
    { id: "warehouses", label: "Warehouses", x: 13.1, y: 66.2, place: "below", icon: "warehouse", pink: true, dotAt: 0.58 },
    { id: "carriers", label: "Carriers", x: 86.7, y: 66.2, place: "below", icon: "truck", pink: false, dotAt: 0.58 },
    { id: "distributors", label: "Distributors", x: 31.5, y: 87.3, place: "left", icon: "bars", pink: true, dotAt: 0.6 },
    { id: "finance", label: "Finance", x: 68.3, y: 87.3, place: "right", icon: "file", pink: false, dotAt: 0.6 },
];

const HubGlyph = ({ name }) => {
    const stroke = {
        fill: "none",
        stroke: "currentColor",
        strokeWidth: 1.6,
        strokeLinecap: "round",
        strokeLinejoin: "round",
    };

    if (name === "chart") {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path {...stroke} d="M5 16.5 9.2 11l3.1 2.6L18.5 7" />
                <path {...stroke} d="M15.2 7H18.5V10.2" />
            </svg>
        );
    }
    if (name === "store") {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path {...stroke} d="M4 9.5 5.2 5.5h13.6L20 9.5" />
                <path {...stroke} d="M4 9.5c0 1.3 1.1 2.1 2.3 2.1S8.6 10.8 8.6 9.5c0 1.3 1 2.1 2.2 2.1s2.1-.8 2.1-2.1c0 1.3 1 2.1 2.3 2.1 1.2 0 2.2-.8 2.2-2.1" />
                <path {...stroke} d="M6.2 11.6V19h11.6v-7.4" />
                <path {...stroke} d="M10 19v-3.6h4V19" />
            </svg>
        );
    }
    if (name === "barsChart") {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path {...stroke} d="M4 19.5h16" />
                <path {...stroke} d="M7 19.5V13M12 19.5V8.5M17 19.5V5.5" />
            </svg>
        );
    }
    if (name === "user") {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle {...stroke} cx="12" cy="8" r="3" />
                <path {...stroke} d="M6.4 19.2c.9-3.1 2.9-4.6 5.6-4.6s4.7 1.5 5.6 4.6" />
            </svg>
        );
    }
    if (name === "warehouse") {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path {...stroke} d="M3 10.5h18" />
                <path {...stroke} d="M4.5 10.5V19.5h15V10.5" />
                <path {...stroke} d="M3 10.5 6.2 6.5h11.6L21 10.5" />
                <path {...stroke} d="M8 19.5v-5h4.2v5" />
                <path {...stroke} d="M14.8 13.2h3.2v2.2h-3.2" />
            </svg>
        );
    }
    if (name === "truck") {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path {...stroke} d="M3 7.5h10.5V16H3z" />
                <path {...stroke} d="M13.5 10.5H18l2.8 3.1V16H13.5" />
                <circle {...stroke} cx="7" cy="17.4" r="1.5" />
                <circle {...stroke} cx="17.2" cy="17.4" r="1.5" />
            </svg>
        );
    }
    if (name === "bars") {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect {...stroke} x="4" y="5" width="4.2" height="14" rx="1" />
                <rect {...stroke} x="9.9" y="5" width="4.2" height="14" rx="1" />
                <rect {...stroke} x="15.8" y="5" width="4.2" height="14" rx="1" />
            </svg>
        );
    }
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path {...stroke} d="M7 3.8h7.2L18.5 8v11.2a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4.8a1 1 0 0 1 1-1z" />
            <path {...stroke} d="M14 3.8V8h4.5" />
            <path {...stroke} d="M9 12.8h6M9 16.2h4" />
        </svg>
    );
};

const PortalCube = () => (
    <svg viewBox="0 0 80 80" aria-hidden="true">
        <defs>
            <linearGradient id="loginCoreTop" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="100%" stopColor="#e4d4ff" />
            </linearGradient>
            <linearGradient id="loginCoreLeft" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#a78bfa" />
                <stop offset="100%" stopColor="#4c3cc9" />
            </linearGradient>
            <linearGradient id="loginCoreRight" x1="1" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ff8ed0" />
                <stop offset="100%" stopColor="#ed008c" />
            </linearGradient>
        </defs>
        <polygon points="40,14 62,26 40,38 18,26" fill="url(#loginCoreTop)" />
        <polygon points="18,26 40,38 40,62 18,50" fill="url(#loginCoreLeft)" />
        <polygon points="62,26 40,38 40,62 62,50" fill="url(#loginCoreRight)" />
        <circle cx="40" cy="14" r="2.2" fill="#fff" />
        <circle cx="18" cy="26" r="2.2" fill="#fff" />
        <circle cx="62" cy="26" r="2.2" fill="#fff" />
        <circle cx="40" cy="38" r="1.8" fill="#fff" />
        <circle cx="18" cy="50" r="1.8" fill="#fff" />
        <circle cx="62" cy="50" r="1.8" fill="#fff" />
        <circle cx="40" cy="62" r="2.2" fill="#fff" />
    </svg>
);

const LoginSuccessPrompt = ({ session, onContinue }) => {
    const user = session?.user;
    const pendingReview = user?.userType === "TEMP_USER";
    const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
    const roleLabel = formatRole(user?.userType);

    useEffect(() => {
        const timer = window.setTimeout(onContinue, SETUP_MS);
        return () => window.clearTimeout(timer);
    }, [onContinue]);

    return (
        <div className="login-success-overlay">
            <div
                className="login-success-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="login-success-title"
                aria-describedby="login-success-status"
            >
                <div className={`login-success-mark${pendingReview ? " is-pending" : ""}`} aria-hidden="true">
                    <i className={`bi ${pendingReview ? "bi-hourglass-split" : "bi-check-lg"}`}></i>
                </div>
                <p className="login-success-kicker">
                    {pendingReview ? "Verification in progress" : "Signed in"}
                </p>
                <h2 id="login-success-title">
                    {pendingReview ? "Your account is under review" : "Welcome back"}
                    {!pendingReview && displayName ? (
                        <span className="login-success-name">{displayName}</span>
                    ) : null}
                </h2>
                <p className="login-success-copy">
                    {pendingReview
                        ? "Sign-in was received. Opening the portal with limited access until verification is complete."
                        : "Your session is ready. Opening your dashboard."}
                </p>
                <div className="login-success-meta">
                    <span className="login-success-role">{roleLabel}</span>
                    {user?.email ? <span className="login-success-email">{user.email}</span> : null}
                </div>
                <div className="login-success-setup">
                    <div className="login-success-track" aria-hidden="true">
                        <span className="login-success-bar" style={{ animationDuration: `${SETUP_MS}ms` }}></span>
                    </div>
                    <p id="login-success-status" className="login-success-status" role="status">
                        Setting up your dashboard
                    </p>
                </div>
            </div>
        </div>
    );
};

const Login = () => {
    const [formData, setFormData] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [pendingSession, setPendingSession] = useState(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const enteredRef = useRef(false);
    const continueToPortal = useCallback(() => {
        if (enteredRef.current || !pendingSession) return;
        enteredRef.current = true;
        dispatch(
            login({
                ...pendingSession.user,
                branding: pendingSession.branding ?? null,
            })
        );
        dispatch(setToken({
            accessToken: pendingSession.accessToken,
            refreshToken: pendingSession.refreshToken,
        }));
    }, [dispatch, pendingSession]);

    const handleLogin = async (event) => {
        event?.preventDefault();
        if (submitting || pendingSession) return;
        setSubmitting(true);
        try {
            const res = await userLogin({
                email: formData?.email,
                password: formData?.password
            })
            if (!res) {
                return;
            }
            setPendingSession({
                user: res.user,
                branding: res.branding ?? null,
                accessToken: res.accessToken,
                refreshToken: res.refreshToken,
            });
        } catch (err) {
            toast.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="login-page">
            {pendingSession ? (
                <LoginSuccessPrompt session={pendingSession} onContinue={continueToPortal} />
            ) : null}
            <div className="login-left">
                <div className="login-logo-section">
                    <img
                        src="/trangile-logo-white.png"
                        alt="Trangile"
                        className="login-brand-logo"
                    />
                </div>
                <div className="hero-frame">
                    <div className="hub-copy">
                        <h2>One Portal for Every<br />Partner Interaction</h2>
                        <p>Orders, shipments, inventory and invoices, connected<br />and AI-driven.</p>
                    </div>

                    <div className="hub-stage">
                        <svg className="hub-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                            {HUB_NODES.map((node) => (
                                <line
                                    key={node.id}
                                    x1={HUB_CENTER.x}
                                    y1={HUB_CENTER.y}
                                    x2={node.x}
                                    y2={node.y}
                                    vectorEffect="non-scaling-stroke"
                                />
                            ))}
                        </svg>
                        {HUB_NODES.map((node) => (
                            <span
                                key={`${node.id}-dot`}
                                className={`hub-dot${node.pink ? " is-pink" : ""}`}
                                style={{
                                    left: `${HUB_CENTER.x + (node.x - HUB_CENTER.x) * node.dotAt}%`,
                                    top: `${HUB_CENTER.y + (node.y - HUB_CENTER.y) * node.dotAt}%`,
                                }}
                            />
                        ))}
                        {HUB_NODES.map((node) => (
                            <div
                                key={node.id}
                                className={`hub-node hub-node--${node.place}`}
                                style={{ "--nx": `${node.x}%`, "--ny": `${node.y}%` }}
                            >
                                <span className="hub-node-ring">
                                    <HubGlyph name={node.icon} />
                                </span>
                                <span className="hub-node-label">{node.label}</span>
                            </div>
                        ))}
                        <div className="hub-core" style={{ left: `${HUB_CENTER.x}%`, top: `${HUB_CENTER.y}%` }}>
                            <PortalCube />
                        </div>
                    </div>

                    <div className="hub-pills">
                        <span>Orders & ASN</span>
                        <span>Shipments & Inventory</span>
                        <span>Invoice Matching</span>
                        <span>AI Insights</span>
                    </div>
                    <div className="hub-pills hub-pills--single">
                        <span>Multi-Partner Management</span>
                    </div>
                    <div className="hub-foot">
                        <span>Secure & Reliable</span>
                        <span>Fast Processing</span>
                        <span>24/7 Support</span>
                    </div>
                </div>
            </div>

            <div className="login-right login-panel">
                <div className="login-stack">
                    <form className="login-box login-card" onSubmit={handleLogin}>
                        <h2>Sign in</h2>
                        <p className="login-lead">Use your work email to continue.</p>

                        <label className="login-field">
                            <span>Email</span>
                            <input
                                type="email"
                                name="email"
                                autoComplete="email"
                                placeholder="name@company.com"
                                value={formData?.email || ""}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                        </label>

                        <div className="login-field">
                            <span>Password</span>
                            <div className="login-password">
                                <input
                                    id="login-password"
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    autoComplete="current-password"
                                    placeholder="Enter your password"
                                    value={formData?.password || ""}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                />
                                <button
                                    type="button"
                                    className="login-reveal"
                                    onClick={() => setShowPassword((open) => !open)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    {showPassword ? "Hide" : "Show"}
                                </button>
                            </div>
                        </div>

                        <div className="actions">
                            <Link to="/forgot-password">Forgot Password?</Link>
                        </div>
                        <button className="login-submit" type="submit" disabled={submitting}>
                            {submitting ? "Signing in" : "Sign in"}
                        </button>
                        <div className="signup-link text">
                            {/* Don't have an account? <span onClick={() => navigate("/self-signup")}>Self Sign up</span> */}
                        </div>
                    </form>
                    <button type="button" className="login-interest" onClick={() => navigate("/showInterest")}>
                        Show interest
                    </button>
                </div>
            </div>
        </div>
    ); 
}

export default Login;